import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import React, { useRef, useState, useCallback } from "react";
import { useChat } from "../../Hooks/ChatContext";
import { useEffect } from "react";
import NewChatUsers from "./NewChatUsers";
import { sort_array_based_on_createdAt } from "src/utils/constants";
import CustomDrawer from "src/components/FormsDrewer/CustomDrawer";
import { _check_chat_existing } from "src/DAL/Chat/Chat";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import GrLoader from "src/design-system/components/GrLoader";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrSearch from "src/design-system/components/GrSearch";
import GrCountBadge from "src/design-system/components/GrCountBadge";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import ChatListItem, { todayKey } from "./ChatListItem";

const paneSx = {
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  height: "100%",
  borderRight: `1px solid ${gr.color.line}`,
  fontFamily: gr.font.family,
  "@media (max-width: 499.95px)": { borderRight: 0 },
};

const headSx = {
  display: "flex",
  flexDirection: "column",
  gap: gr.space[3],
  p: gr.space[4],
  minHeight: `calc(${gr.control.md} + ${gr.space[4]} * 2)`,
  boxSizing: "border-box",
  borderBottom: `1px solid ${gr.color.line}`,
  flex: "none",
};

const headRowSx = {
  minHeight: gr.control.md,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[2],
};

const listSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarWidth: "thin",
};

const loadMoreSx = {
  textAlign: "center",
  py: gr.space[3],
  fontSize: gr.font.size.sm,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.brandDark,
  cursor: "pointer",
};

export default function ChatList() {
  const [drawerState, setDrawerState] = useState(false);
  // window.location (same value; the bare `location` global was flagged by lint).
  const chat_id = new URLSearchParams(window.location.search).get("chat_id");
  const load_more_path = "api/chat/list_chat_by_user?page=0&limit=30";
  const observer = useRef(null);
  const [state, setState] = useState({
    search_text: "",
  });

  const handleOpenDrawer = () => {
    setDrawerState(true);
  };

  const handleCloseDrawer = () => {
    setDrawerState(false);
  };

  const { userInfo } = useContentSetting();

  const {
    unreadChatCounts,
    chatList,
    handleSetSelectedChat,
    selectedChatUser,
    get_chat_list,
    handleAddChat,
    chatLoading,
    hasMorePath,
    hasTotalPages,
    hasLoading,
    setHasLoading,
    setChatList,
  } = useChat();

  const handleClick = (chat) => {
    handleSetSelectedChat(chat);
  };

  const loadMoreData = () => {
    setHasLoading(true);
    get_chat_list(hasMorePath, state.search_text, true);
  };

  const lastBookElementRef = useCallback(
    (node) => {
      if (hasLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasTotalPages) {
          loadMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasLoading, hasTotalPages, hasMorePath, observer]
  );

  const handleAddChatNew = async (user_id) => {
    const result = await _check_chat_existing(user_id);
    let selected_obj = {};
    if (result.code === 200) {
      let my_profile = {
        name: userInfo.name,
        profileImage: userInfo.image.thumbnail_1,
        unread_message_count: 0,
        isOnline: true,
        id: userInfo._id,
      };
      let receiver_profile = {
        name: result.memberInfo.name,
        profileImage: result.memberInfo.profile_image,
        isOnline: result.memberInfo.is_online,
        unread_message_count: 0,
        id: result.memberInfo.id,
      };
      selected_obj = {
        chatId: result.chatId,
        my_profile,
        receiver_profile,
        members: [my_profile, receiver_profile],
      };
      setChatList((prev) => {
        return [selected_obj, ...prev];
      });
      handleSetSelectedChat(selected_obj);
    }
  };

  useEffect(() => {
    if (chat_id) {
      if (chatList.length > 0) {
        let find_chat = chatList.find(
          (chat) => chat?.receiver_profile?.id == chat_id
        );
        if (find_chat) {
          handleSetSelectedChat(find_chat);
        } else {
          handleAddChatNew(chat_id);
        }
      } else {
        handleAddChatNew(chat_id);
      }
    }
  }, [chat_id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    get_chat_list(load_more_path, state.search_text);
  }, [state.search_text]);

  let sortedChatList = [];

  if (chat_id) {
    sortedChatList = [
      ...chatList.filter((chat) => chat.receiver_profile.id === chat_id),
      ...chatList.filter((chat) => chat.receiver_profile.id !== chat_id),
    ];
  } else {
    sortedChatList = sort_array_based_on_createdAt(chatList);
  }

  const today = todayKey();

  return (
    <>
      <Box sx={paneSx}>
        <Box sx={headSx}>
          {/* One row: search, unread count, new chat (+). No title, as before. */}
          <Box sx={headRowSx}>
            <GrSearch
              placeholder="Search"
              label="Search"
              value={state.search_text}
              name="search_text"
              onChange={handleChange}
              // 40px like the "+" button beside it (the list header then matches the conversation header).
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-input:not(textarea)": {
                  py: `calc((${gr.control.md} - 20px) / 2)`,
                },
              }}
            />
            {unreadChatCounts > 0 && (
              <GrCountBadge
                count={unreadChatCounts}
                tone="alert"
                label={`${unreadChatCounts} unread chats`}
              />
            )}
            {!drawerState && (
              <Tooltip title="New Message" placement="top">
                <GrIconButton
                  variant="soft"
                  aria-label="New message"
                  onClick={handleOpenDrawer}
                >
                  <GrIcon icon={icons.plus} />
                </GrIconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        {chatLoading ? (
          <GrLoader />
        ) : (
          <Box sx={listSx}>
            {/* Search with no matches: say so instead of a blank list (same "… Not Found" wording as Friends). */}
            {state.search_text && sortedChatList.length === 0 ? (
              <GrEmptyState
                compact
                icon={<GrIcon icon={icons.chat} />}
                title="Chats Not Found"
              />
            ) : null}
            {sortedChatList.map((chat, i) => {
              return (
                <ChatListItem
                  key={chat?.chatId ?? i}
                  chat={chat}
                  today={today}
                  selected={chat?.receiver_profile?.id === selectedChatUser?.id}
                  onClick={() => handleClick(chat)}
                />
              );
            })}
            {hasTotalPages ? (
              <Box sx={loadMoreSx}>
                <span
                  ref={lastBookElementRef}
                  onClick={loadMoreData}
                  id="load-more-feed"
                >
                  {hasLoading ? (
                    <GrLoader size="sm" inline />
                  ) : (
                    "Load More"
                  )}
                </span>
              </Box>
            ) : (
              ""
            )}
          </Box>
        )}
      </Box>
      <CustomDrawer
        isOpenDrawer={drawerState}
        onOpenDrawer={handleOpenDrawer}
        onCloseDrawer={handleCloseDrawer}
        pageTitle={`New Message`}
        componentToPassDown={
          <NewChatUsers
            onCloseDrawer={handleCloseDrawer}
            handleAddChat={handleAddChat}
          />
        }
      />
    </>
  );
}
